import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { fileUrl, money } from "@/lib/api";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { items, update, remove, restore, subtotal } = useCart();
  const navigate = useNavigate();

  const removeLine = (it, index) => {
    remove(it.product_id, it.size);
    toast("Item removed", {
      description: `${it.name}${it.size ? ` (${it.size})` : ""}`,
      action: { label: "Undo", onClick: () => restore(it, index) },
    });
  };

  if (items.length === 0) return (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <ShoppingBag className="mx-auto h-12 w-12 text-kys-blush-border" />
      <h1 className="mt-5 font-serif text-3xl text-kys-ink">Your bag is empty</h1>
      <p className="mt-2 text-sm text-kys-muted">Discover handmade pieces ready to ship.</p>
      <Link to="/shop" className="mt-6 inline-flex rounded-full bg-kys-ink px-7 py-3 text-sm font-semibold text-white hover:bg-kys-accent">Shop Ready to Ship</Link>
    </div>
  );

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="font-serif text-4xl tracking-tight text-kys-ink">Your Bag</h1>
      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="divide-y divide-kys-blush-border rounded-md border border-kys-blush-border bg-white">
            {items.map((it, index) => (
              <div key={`${it.product_id}-${it.size}`} data-testid={`cart-line-${it.slug}`} className="flex gap-4 p-4">
                <Link to={`/product/${it.slug}`} className="h-28 w-24 shrink-0 overflow-hidden rounded border border-kys-blush-border bg-kys-blush">
                  {it.image && <img src={fileUrl(it.image)} alt={it.name} className="h-full w-full object-cover" />}
                </Link>
                <div className="flex flex-1 flex-col">
                  <div className="flex justify-between gap-2">
                    <Link to={`/product/${it.slug}`} className="text-sm font-medium text-kys-ink hover:text-kys-accent">{it.name}</Link>
                    <span className="font-mono text-sm">{money(it.price * it.quantity)}</span>
                  </div>
                  {it.size && <p className="mt-0.5 text-xs text-kys-muted">Size: {it.size}</p>}
                  <div className="mt-auto flex items-center justify-between pt-3">
                    <div className="flex items-center rounded border border-kys-blush-border">
                      <button data-testid={`cart-decrease-${it.slug}`} aria-label="Decrease quantity" onClick={() => update(it.product_id, it.size, Math.max(1, it.quantity - 1))} className="px-2.5 py-1.5"><Minus className="h-3.5 w-3.5" /></button>
                      <span data-testid={`cart-qty-${it.slug}`} className="w-7 text-center text-sm">{it.quantity}</span>
                      <button data-testid={`cart-increase-${it.slug}`} aria-label="Increase quantity" onClick={() => update(it.product_id, it.size, it.quantity + 1)} className="px-2.5 py-1.5"><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                    <button data-testid={`cart-remove-${it.slug}`} onClick={() => removeLine(it, index)}
                      className="flex items-center gap-1.5 rounded-full border border-kys-blush-border px-3 py-1.5 text-xs font-medium text-kys-charcoal transition-colors hover:border-destructive hover:text-destructive">
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="rounded-md border border-kys-blush-border bg-white p-6">
            <h2 className="font-serif text-2xl text-kys-ink">Summary</h2>
            <div className="mt-4 flex justify-between text-sm"><span className="text-kys-charcoal">Subtotal</span><span className="font-mono">{money(subtotal)}</span></div>
            <div className="mt-2 flex justify-between text-sm text-kys-muted"><span>Shipping</span><span>Calculated at checkout</span></div>
            <div className="mt-2 flex justify-between text-sm text-kys-muted"><span>Tax</span><span>Calculated at checkout</span></div>
            <button data-testid="checkout-btn" onClick={() => navigate("/checkout")}
              className="mt-6 w-full rounded-full bg-kys-ink py-3.5 text-sm font-semibold text-white transition hover:bg-kys-accent">
              Checkout
            </button>
            <p className="mt-3 text-center text-xs text-kys-muted">U.S. shipping only</p>
          </div>
        </div>
      </div>
    </div>
  );
}
